import React from 'react';
import get from 'lodash/get';
import has from 'lodash/has';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { groupLocations } from 'webapp-common-ui/src/utils/groupLocations';
import { ITEM_TYPE } from './constants';
import middlewareState from '../../utils/middlewareState';

export const isIE = () => document.documentMode || /Edge/.test(navigator.userAgent);

export const cloneReactElement = (children, props) => React.cloneElement(children, { ...children.props, ...props });

export function getErrorMessageKey(err) {
  let errorMessageKey = get(err, 'response.body.message', err.message || err || '');

  if (errorMessageKey && errorMessageKey.indexOf(' ') > -1) {
    errorMessageKey = errorMessageKey.replace(/\s/g, '_').toUpperCase();
  }

  return errorMessageKey;
}

export function containsAllPaymentOptions(pricingData) {
  return window.FanWallet
    ? fanWalletContainsAllPaymentOptions(pricingData)
    : legacyContainsAllPaymentOptions(pricingData);
}

function fanWalletContainsAllPaymentOptions(pricingData) {
  return get(pricingData, 'paymentData.containsPayout', false);
}

function legacyContainsAllPaymentOptions(pricingData) {
  return has(pricingData, 'payoutOption');
}

export function getErrorMessage(err, messages, defaultText) {
  if (!err) {
    return defaultText;
  }

  const errorMessageKey = getErrorMessageKey(err);

  return messages[errorMessageKey]
    || messages[`payment.errors.${errorMessageKey}`]
    || messages[`price.errors.${errorMessageKey}`]
    || defaultText;
}


export function buildPriceHelpRequest(locations, eventId, currencyCode) {
  const sortedLocations = sortBy(locations, 'seat');

  return {
    eventId,
    section: [].concat(map(sortedLocations, 'section')).shift(),
    row: [].concat(map(sortedLocations, 'row')).shift(),
    seatFrom: [].concat(map(sortedLocations, 'seat')).shift(),
    seatThru: [].concat(map(sortedLocations, 'seat')).pop(),
    currencyCode,
  };
}

/**
 * This function is used to build the request object for making the currently listed sections call.
 * @param sections array of sections e.g. ["104", "102"]
 * @param eventId hostEventId
 * @param currencyCode
 * @returns {{eventId: *, section: *, currencyCode: *}}
 */
export function buildCurrentlyListedRequest(sections, eventId, currencyCode) {
  return {
    eventId,
    section: sections.join(),
    currencyCode,
  };
}

export function renderSeatLabels(tickets, className) {
  return (map(groupLocations(tickets), (item, i) => (
    <div className={className} key={i}>
      {item}
    </div>
  ))
  );
}


/**
 * This function resolves or rejects a promise,
 * where the promise is resolved if the booleanFunc return back true before the timeout occurs,
 * else we will get a reject from the promise.
 *
 * @param retryInterval At what rate (in ms) the booleanFunc has to called to check if the action succeeded.
 *        [default value = 200ms]
 * @param timeout How long (in ms) should the timeout occur before rejecting the promise,
 *        [default value = 1000ms].
 * @param booleanFunc A function which will evaluate to either true or false
 * @param maxRetries  How many retries we should do with the booleanFunc,
 *        [default value = 100].
 * @returns {Promise<any>}
 */
export function raceForSuccessOrTimeout(retryInterval, timeout, booleanFunc, maxRetries) {
  let intervalId;
  let timeoutId;
  let retries = maxRetries == null ? 100 : maxRetries;
  const retryIntervalMs = retryInterval == null ? 200 : retryInterval;
  const timeoutMs = timeout == null ? 2000 : timeout;

  const clearTimers = () => {
    clearInterval(intervalId);
    clearTimeout(timeoutId);
  };

  // This is the retry promise which will continuously retry the function given to it using retryInterval,
  // if the function succeeds then it resolves the promise else it will reject the promise.
  const retryPromise = new Promise(((resolve, reject) => {
    intervalId = setInterval(() => {
      if (booleanFunc()) {
        clearTimers();
        resolve('SUCCESS');
      }
      // We have max retries to not run into issue where this retry promise never ends.
      retries -= 1;
      if (retries < 0) {
        reject('MAX_RETRIES_REACHED');
      }
    }, retryIntervalMs);
  }));

  // This is the timeout promise which will wait until given timeout and then executes the logic in it.
  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      clearTimers();
      /* We are checking here one last time because there might be
       cases where in the last millisecond the function satisfies. */
      if (booleanFunc()) {
        resolve('SUCCESS');
      } else {
        reject('TIMEOUT');
      }
    }, timeoutMs);
  });

  return Promise.race([ retryPromise, timeoutPromise ]);
}


export function isValidPriceHelpData(priceHelpData) {
  const postedLowBound = get(priceHelpData, 'Posted.LowBound');
  const postedHighBound = get(priceHelpData, 'Posted.HighBound');
  const priceIntervals = get(priceHelpData, 'Posted.PriceIntervals');
  const lowestRecommendedPrice = get(priceHelpData, 'LowestRecommendedPrice');
  const highestRecommendedPrice = get(priceHelpData, 'HighestRecommendedPrice');

  return postedLowBound !== undefined && postedHighBound !== undefined &&
    lowestRecommendedPrice !== undefined && highestRecommendedPrice !== undefined &&
    !isEmpty(priceIntervals);
}

function isNumericValue(value) {
  if (typeof value === 'number') return isFinite(value);
  if (typeof value === 'string' && value.trim() !== '') return isFinite(Number(value));
  return false;
}

function isPresentIdValue(value) {
  if (typeof value === 'number') return isFinite(value);
  if (typeof value === 'string') return value.trim() !== '';
  return false;
}

function currencyConversionMatches(currencyConversion, currencyCode) {
  if (!Array.isArray(currencyConversion) || currencyConversion.length === 0) return true;
  const target = currencyCode.trim().toUpperCase();
  return currencyConversion.some((entry) => {
    const entryCurrency = entry && entry.currency;
    if (typeof entryCurrency !== 'string' || entryCurrency.trim() === '') return false;
    return entryCurrency.trim().toUpperCase() === target;
  });
}

function currenciesMatch(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  return a.trim().toUpperCase() === b.trim().toUpperCase();
}

export function isRenderableSellInstantlyOffer(listingOffer, options) {
  const requireTickets = !!(options && options.requireTickets);
  const checkExpectedCurrency = !!(options && Object.prototype.hasOwnProperty.call(options, 'expectedCurrency'));
  if (!listingOffer) return false;
  if (!isPresentIdValue(listingOffer.offerId)) return false;
  const calculated = listingOffer.calculatedFeeAndPrice;
  if (!calculated) return false;
  if (!isNumericValue(calculated.offerPrice) || Number(calculated.offerPrice) <= 0) return false;
  if (!isNumericValue(calculated.sellerPayout)) return false;
  if (!isNumericValue(calculated.feeAmount)) return false;
  if (typeof calculated.currencyCode !== 'string' || calculated.currencyCode.trim() === '') return false;
  if (!currencyConversionMatches(listingOffer.currencyConversion, calculated.currencyCode)) return false;
  if (checkExpectedCurrency && !currenciesMatch(calculated.currencyCode, options.expectedCurrency)) return false;
  if (requireTickets) {
    const tickets = listingOffer.tickets;
    if (!Array.isArray(tickets) || tickets.length === 0) return false;
  }
  return true;
}

export function getIEVersion() {
  const userAgent = window.navigator.userAgent;

  const msie = userAgent.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    return parseInt(userAgent.substring(msie + 5, userAgent.indexOf('.', msie)), 10);
  }

  const trident = userAgent.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = userAgent.indexOf('rv:');

    return parseInt(userAgent.substring(rv + 3, userAgent.indexOf('.', rv)), 10);
  }

  const edge = userAgent.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return parseInt(userAgent.substring(edge + 5, userAgent.indexOf('.', edge)), 10);
  }

  // other browser
  return -1;
}

export const isBrowserIE11 = () => {
  const IEVersion = getIEVersion();

  return IEVersion < 12 && IEVersion > 0;
};

export function formatNumber(number, fractionDigits = 2, currencyCode = 'USD', locale = 'en-US') {
  return new Intl.NumberFormat(locale,
    {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      style: 'currency',
      currency: currencyCode,
    }).format(number);
}

export const sortPostedPriceHelpRanges = (postedPriceIntervals) => {
  postedPriceIntervals.sort((firstInterval, secondInterval) => {
    if (firstInterval.Min > secondInterval.Min) {
      return 1;
    }

    return (secondInterval.Min > firstInterval.Min) ? -1 : 0;
  });

  return postedPriceIntervals;
};

// We are adding edge intervals on the min and max intervals of the
// data to represent when the user inputs an out of range price.
// We will take the min and max and add ranges. For the min edge interval
// we take current lowest value - 0.01 and the max is -2.
// The max edge interval we will take in the highest value + 0.01 as it's min
// and have a max of our highest supported price plus a 2 for extra padding.
export const addPriceHelpEdgeIntervals = (postedPriceIntervals) => {
  if (postedPriceIntervals.length < 1) {
    return postedPriceIntervals;
  }
  const newIntervals = [ ...postedPriceIntervals ];
  // Make min edge
  const edgeMinInterval = {
    Count: 0,
    Min: -2,
    Max: postedPriceIntervals[0].Min - 0.01,
  };

  // Make max edge
  const edgeMaxInterval = {
    Count: 0,
    Min: postedPriceIntervals[postedPriceIntervals.length - 1].Max + 0.01,
    Max: 100001.99,
  };

  // Add edgeMinInterval to the front
  newIntervals.unshift(edgeMinInterval);
  // Add edgeMaxInterval to the end
  newIntervals.push(edgeMaxInterval);

  return newIntervals;
};

export const isPriceInRange = (pricePerTicket, floor, ceiling) => pricePerTicket >= floor && pricePerTicket <= ceiling;

export const hash = (item, idx = 0) => {
  /* DJB Hash: Hash function for use in React Array keys since we cannot use idx from
  iterators. The only additons made to the hasher is a trycatch block and the use
  of an optional idx param for use as a salt.

  https://stackoverflow.com/questions/10696223/reason-for-5381-number-in-djb-hash-function
  */
  let str = idx || '';

  try {
    str = JSON.stringify(item) + idx;
  } catch (e) {
    (() => {})();
  }

  let hash = 5381;
  let i = str.length;
  while (i) hash = (hash * 33) ^ str.charCodeAt(--i); // eslint-disable-line no-bitwise, no-plusplus

  return hash >>> 0; // eslint-disable-line no-bitwise
};

/**
 * This function returns a label for ticket rows:
 * Sec: <section>, Row: <row>, Seat<seat>
 * If a property is not present then it does not add
 * the property string and value. This case is important
 * for GA. Example:
 * Sec: GA, Row: GA7
 *
 * For GA if Row or Seat is equal to Sec then the property is not
 * added.
 *
 * If ticket passed in is null or undefined we return an empty string
 *
 */
export const createTicketListSeatLabel = (ticket) => {
  const ticketLabels = [];

  if (!ticket) {
    return '';
  }

  const section = ticket.Section ? ticket.Section : undefined;
  if (section) {
    ticketLabels.push(`Sec ${ticket.Section}`);
  }

  // Check if ticket is GA to only show unique properties compared with the section
  const isRowAdded = ticket.IsGA ? ticket.Row !== section : true;
  if (ticket.Row && isRowAdded && section) {
    ticketLabels.push(`Row ${ticket.Row}`);
  }

  // Check if ticket is GA to only show unique properties compared with the section
  const isSeatAdded = ticket.IsGA ? ticket.Seat !== section : true;
  if (ticket.Seat && isSeatAdded && section) {
    ticketLabels.push(`Seat ${ticket.Seat}`);
  }

  return ticketLabels.join(', ');
};

/**
 * Takes in an array of locations and returns true
 * if locations has a ticket with generalAdmission set to true
 */
export const locationsContainGA = locations => locations.find(ticket => ticket.generalAdmission);

/* This function is the same function from myee with modifications in it to make
* it not need any pass-ins [other than eligibility]. We may want to copy this to
* myee during refactoring. Then remove this note */
export const isEventEligibleFor = (eligibility) => {
  const myeeState = middlewareState.myee.getState();
  const id = get(myeeState, 'posting.event.id', null);
  const items = get(myeeState, 'order.order.items', []);

  const ALLOWED_ITEM_TYPES = [ ITEM_TYPE.EVENT, ITEM_TYPE.SERIES ];

  const eligibleItems = items
    .filter(item => item && ALLOWED_ITEM_TYPES.indexOf(item.type) !== -1)
    .filter(item => find(item.eligibilities, {
      type: eligibility,
      eligible: true,
    }));

  const isEligible = Boolean(
    find(eligibleItems, { id }),
  );

  return isEligible;
};

export const getSellType = (isListingToSellInstantly, isActiveListing, isEditListingToSellInstantly) => {
  const isSellInstantly = isEventEligibleFor('SELL_INSTANTLY');
  const isResale = isEventEligibleFor('RESALE');
  if (isListingToSellInstantly != null && isListingToSellInstantly) {
    return 'SI_PRICE_TICKETS';
  } else if (isActiveListing != null && isActiveListing) {
    if (isEditListingToSellInstantly != null && isEditListingToSellInstantly) {
      return 'SI_EDIT_LISTING';
    }
    return 'SI_ACTIVE_LISTING';
  }
  return (isSellInstantly && !isResale ? 'SI_NON_TM_PLUS' : 'SI_INITIAL_OFFER');
};

export const getSellTypeUrl = (ticketURL, isListingToSellInstantly, isActiveListing, isEditListingToSellInstantly) => {
  let result = ticketURL;
  result += `?sell_type=${getSellType(isListingToSellInstantly, isActiveListing, isEditListingToSellInstantly)}`;
  return result;
};


export const redirectToUrl = (url) => {
  const redirectLink = new URL(url);
  window.location.href = redirectLink.href;
};
